<!--
  StorefrontHero.vue -- P5 storefront (reusable hero carousel).

  Card: Live Storefront - Home/Category/Product (ADR 0060/0061)
  https://trello.com/c/q7EQ3a0b
  Pebble: P5 -- Home + Category pages + route tree (frontend).

  Extracted VERBATIM from StorefrontViewShopper's inline hero carousel
  (issue #2178 / DEFECT A) so the new Home page and the legacy single page
  can share ONE hero implementation. The legacy page still renders (P8
  retires it); this component is the shared seam it and the Home page both
  mount.

  Contract (the caller owns fetch + sanitize; this component owns render +
  no-crop geometry):

    slides = [{ id, headline, imageUrl, clickThroughUrl }]

  Each slide's fields are ALREADY sanitized by the caller: the headline is
  plain text (bound with {{ }}, never v-html) and clickThroughUrl is an
  http(s)/relative href or null (a non-clickable slide). This component
  never re-fetches or re-sanitizes.

  No-crop geometry: the band height = the WIDEST slide scaled to the column
  width (bandHeight = columnWidth / maxAspectRatio), capped at HERO_MAX_HEIGHT
  so a tall/narrow slide can't tower over the page. Every narrower slide fits
  that height with white side padding (object-fit: contain). No image is ever
  upscaled beyond its natural pixel size. See utils/heroCarouselLayout.js --
  the DOM-free arithmetic lives there; this component owns measurement.

  SPLIT BAND (2026-08-14 layout gap fix, item 4 --
  docs/planning/2026-08-14-storefront-layout-gap-vs-company-store-comp.md).
  The hero renders as two columns: a COPY column (the store name as a large
  brand-ink headline) beside a MEDIA column (the carousel above). This is
  what the comp shows, and it is also what makes the existing no-upscale rule
  survivable: real store hero art is small and square-ish (the T+L slides
  measure 495x497, 338x484, 499x322), so across the full content width those
  pixels rendered as a ~219px photo floating in a wide empty band. Confining
  the media column to ~45% of the band makes the SAME pixels adequate --
  no upscaling (blurry) and no aggressive cropping. The alternative,
  reverting to a full-bleed photo band, forces one or the other.

  The band is full-bleed and its INNER grid is width-capped (.sf-container),
  which is how the comp's photography reaches the right edge of the viewport
  while the copy stays aligned with the page's content column.

  Below 960px the two columns stack (copy above media) -- the split is a
  wide-viewport affordance, and the stacked order falls out of document
  order rather than a second set of rules.

  FULL-BLEED BANNER (2026-08-15). The comp's hero is in fact ONE full-width
  image with its headline, subcopy and tagline baked into the PIXELS -- the
  split band above was built on a misreading of it. Both layouts are real and
  correct for different art, so the hero picks between them from the art
  itself:

    widest measured slide aspect >= 2.2  -> BANNER: full width, no copy
                                            column, no width cap
    anything narrower                    -> SPLIT BAND (above), unchanged

  The threshold's evidence is the measured art: T+L product slides are
  1.00 / 0.70 / 1.55, an ordinary 16:9 photo is 1.78, and the T+L banner crop
  (1448x451) is 3.21 -- see BANNER_ASPECT_THRESHOLD in
  utils/heroCarouselLayout.js. Deriving the mode keeps ONE mechanism ("the
  hero renders the art it is given") instead of adding a Branding field a
  distributor has to understand; it also means the two modes cannot drift
  apart, since both read the same widestSlideAspect measurement.

  The no-upscale rule is deliberately suspended for a banner and ONLY for a
  banner: capping a 1448px banner at its natural width would leave gutters on
  a wide monitor. It fills the width by scaling up instead. Non-banner slides
  keep the natural-size cap and object-fit: contain exactly as before.

  BANNER LETTERBOX (2026-08-26, Jake) -- REVISES the paragraph above.

  The 2026-08-15 decision paired that upscale with object-fit: COVER, and
  argued gutters were "the one failure full-bleed exists to prevent". That
  was wrong for the art this layout exists to show. The band height is capped
  (HERO_BANNER_MAX_HEIGHT); once the cap BINDS, the band is shorter than the
  banner's own ratio wants and `cover` fills it by slicing the overflow off
  the top and bottom -- measured in Chrome at 2560px: 177px of artwork
  discarded; at 3440px, 451px. The T+L / ACME banner has its HEADLINE BAKED
  INTO THE PIXELS, so that crop cut "ACME GOLF SHOP COMPANY STORE" in half.

  Jake, verbatim: "When the viewport is wider than a hero carousel banner
  image, it gets stretched horizontally to fill the viewport and cropped
  vertically, instead of showing a margin on either side."

  Cropping baked-in text is strictly worse than a margin, so the banner now
  uses object-fit: contain AND caps the image's own width to what the capped
  height can show whole (bannerRenderWidth in utils/heroCarouselLayout.js).
  The width cap is the load-bearing half: switching the fit alone would move
  the letterboxing to the TOP AND BOTTOM, which is not what was asked for.

  Below the cap NOTHING changes -- the banner still reaches both viewport
  edges (verified at 1280px and 1920px). The bug was only ever about
  viewports wide enough for the height cap to bind. The SPLIT BAND path is
  untouched: it already used contain with side padding, which is the
  behaviour this change gives the banner.

  Renders NOTHING when there are zero slides (the caller decides the empty
  state / fallback brand image) -- a store name alone never conjures a band.
-->
<template>
  <div
    v-if="hasRenderableSlides"
    class="storefront-hero"
    data-testid="storefront-hero-carousel"
  >
    <div
      class="storefront-hero-band"
      :class="
        isBannerBand
          ? 'storefront-hero-band--banner'
          : 'sf-container'
      "
      data-testid="storefront-hero-band"
      :style="brandAccentStyle"
    >
      <div
        v-if="storeName && !isBannerBand"
        class="storefront-hero-copy"
        data-testid="storefront-hero-copy"
      >
        <!-- Plain text interpolation, never v-html: storeName comes from the
             campaign sign-in payload and is distributor-authored. -->
        <h1
          class="storefront-hero-headline"
          data-testid="storefront-hero-headline"
        >
          {{ storeName }}
        </h1>
      </div>

      <div
        ref="heroCarousel"
        class="storefront-hero-media"
        data-testid="storefront-hero-media"
      >
        <v-carousel
          hide-delimiters
          :show-arrows="hasMultipleSlides ? 'hover' : false"
          :cycle="hasMultipleSlides ? true : false"
          :interval="carouselInterval"
          :height="heroBandHeight"
          class="storefront-hero-carousel"
          width="100%"
        >
          <v-carousel-item
            v-for="slide in slides"
            :key="slide.id"
            data-testid="storefront-hero-slide"
          >
            <component
              :is="slide.clickThroughUrl ? 'a' : 'div'"
              v-bind="
                slide.clickThroughUrl
                  ? {
                      href: slide.clickThroughUrl,
                      target: '_blank',
                      rel: 'noopener noreferrer',
                    }
                  : {}
              "
              class="storefront-hero-slide-link"
            >
              <img
                v-if="hasImage(slide)"
                class="storefront-hero-slide-image"
                data-testid="storefront-hero-slide-image"
                :src="slide.imageUrl"
                :style="slideImageStyle(slide)"
                alt=""
                @load="onSlideImageLoad(slide.id, $event)"
                @error="onSlideImageError(slide.id)"
              />
              <!-- No usable image: a neutral placeholder rather than an
                   <img> the browser will paint as a broken icon. Decorative
                   (aria-hidden) -- it carries no information the shopper
                   needs, and the slide's own headline remains readable. -->
              <div
                v-else
                class="storefront-hero-slide-placeholder"
                data-testid="storefront-hero-slide-placeholder"
                aria-hidden="true"
              ></div>
              <div
                v-if="slide.headline"
                class="storefront-hero-slide-headline"
                data-testid="storefront-hero-slide-headline"
              >
                {{ slide.headline }}
              </div>
            </component>
          </v-carousel-item>
        </v-carousel>
      </div>
    </div>
  </div>
</template>

<script>
import {
  maxAspectRatio,
  bandHeight,
  isBannerAspect,
  bannerRenderWidth,
  HERO_BANNER_MAX_HEIGHT,
} from "@/utils/heroCarouselLayout";
import { storefrontBrandAccentVars } from "@/utils/storefrontBrandAccent";

// Neutral hero band height (px) held until the column width and at least one
// slide's natural aspect ratio are known. Kept in step with HERO_MAX_HEIGHT so
// first paint is stable and never collapses to 0.
const HERO_FALLBACK_HEIGHT = 380;

// Upper bound (px) on the computed hero band height so a tall/narrow-aspect
// slide can't dominate the page. Slides exceeding the cap letterbox to fit
// (object-fit: contain).
//
// Raised from 220 to 380 by the split-band change, and the two numbers are
// measuring different things. At 220 the media column was the FULL content
// width (~1320px), so 220px was already a wide letterbox strip. The media
// column is now 45% of the capped grid less its padding and gap
// ((1320 - 64 - 32) * 0.45 ~= 551px), and the hero art is square-ish
// (495x497, 338x484, 499x322 for T+L) -- at 220px a 497px-tall image would
// letterbox away more than half its height, which is the floating-thumbnail
// look this change exists to remove. 380px lets a square slide fill the
// column's own width without upscaling (the tallest T+L slide is 497px
// natural, so 380 still DOWNscales -- never up), while staying short enough
// that a tall/narrow slide cannot tower over the fold.
const HERO_MAX_HEIGHT = 380;

// Upper bound (px) on a FULL-BLEED BANNER band. Separate from HERO_MAX_HEIGHT
// because the two constrain different geometries: 380 caps a slide inside a
// ~551px media column, while a banner spans the whole viewport. At 1920px the
// T+L banner's own 3.21:1 ratio wants 598px, and clipping that to 380 would
// crop away a third of the artwork -- including the baked-in headline this
// layout exists to show. 620 lets the banner render at its natural ratio on
// every common desktop width (a 2000px viewport is the first that would be
// capped) while still keeping the band under the fold on a laptop.
//
// Above that width the cap BINDS, and what happens then changed on
// 2026-08-26: the banner no longer crops to fill the shortened band, it
// letterboxes horizontally (bannerImageWidth / bannerRenderWidth). So this
// constant now sets the banner's MAXIMUM DRAWN SIZE rather than a crop
// threshold -- raising it makes the art bigger on an ultrawide monitor and
// narrows the side margin; it can no longer cause a vertical crop.
// (The value lives in utils/heroCarouselLayout.js so the category page's
// StorefrontBanner shares it -- GH-3633.)

export default {
  name: "StorefrontHero",

  props: {
    // Render-ready, pre-sanitized slides: [{ id, headline, imageUrl,
    // clickThroughUrl }]. The caller owns fetch + sanitize (headline is
    // plain text; clickThroughUrl is a safe href or null).
    slides: {
      type: Array,
      default: () => [],
    },
    // Auto-advance interval (ms). Non-positive / non-finite falls back to
    // 5000ms so Vuetify never receives 0/negative (which stops the cycle).
    intervalMs: {
      type: Number,
      default: 5000,
    },
    // The store's display name -- the split band's headline (the comp's
    // "Travel + Leisure Company Store"). SAME value the topbar renders
    // (Vuex getStoreName, committed by useStorefrontChrome), passed in by
    // the page rather than read from Vuex here so the two can never
    // disagree about what this store is called (design principle 4) and so
    // this component stays a pure render of its props.
    //
    // Empty/absent collapses the copy column entirely -- a store with no
    // resolved name gets the media band it always had, not an empty slab
    // (principle 5: fail toward the record).
    storeName: {
      type: String,
      default: "",
    },
    // The store's brand color (stores.brand_color). Used ONLY to derive the
    // AA-safe headline ink via storefrontBrandAccentVars -- the same helper
    // the Home page's welcome card uses, so the comp's navy is the store's
    // own color rather than a hardcoded literal, and a light/vivid brand
    // color still yields a legible headline.
    brandColor: {
      type: String,
      default: "",
    },
  },

  data() {
    return {
      // Natural pixel dimensions of each loaded slide image, keyed by slide
      // id: { [id]: { naturalWidth, naturalHeight } }. Populated by @load.
      slideDims: {},
      // Live width (px) of the hero band, tracked via a ResizeObserver so the
      // band height recomputes on resize. Null until measured once.
      heroContainerWidth: null,
      // Slide ids whose image failed to load (404 / deleted object / bad
      // key). Treated exactly like a missing imageUrl so a runtime failure
      // degrades to the placeholder instead of a broken icon.
      failedSlideIds: {},
    };
  },

  computed: {
    // CSS custom properties (--sf-brand-color / --sf-brand-ink) for the
    // headline. Returns {} for a missing/invalid brand color, which leaves
    // the scoped stylesheet's var(..., fallback) defaults in control --
    // never a broken or transparent headline.
    brandAccentStyle() {
      return storefrontBrandAccentVars(this.brandColor);
    },

    // The band renders only when at least one slide carries SOMETHING a
    // shopper can see: an image, a headline, or a click-through.
    //
    // Zero slides was always "render nothing" (the caller owns the empty
    // state). A set of slides that are entirely empty -- no image, no
    // headline, no link -- is that same situation wearing a different hat,
    // and reserving a 380px band for a column of blank placeholders is
    // noise, not a fallback.
    //
    // But an imageless slide WITH a headline or a link is real, authored
    // content: it renders (placeholder + headline), because dropping it
    // would make the storefront disagree with the editor about how many
    // slides exist and would silently swallow the distributor's copy.
    hasRenderableSlides() {
      return (this.slides || []).some(
        (slide) =>
          this.hasImage(slide) ||
          (slide && (slide.headline || slide.clickThroughUrl))
      );
    },

    carouselInterval() {
      const ms = Number(this.intervalMs);
      return Number.isFinite(ms) && ms > 0 ? ms : 5000;
    },

    // Aspect ratio (width/height) of the WIDEST slide whose image has loaded,
    // or null while nothing is measured. The single measurement both the band
    // height and the banner decision read, so the two can never disagree
    // about which slide is setting the band's shape (design principle 4:
    // one source of state).
    //
    // A slide whose image FAILED to load is excluded: onSlideImageError drops
    // it from the <img> render entirely, so keeping its stale dimensions here
    // would let a vanished image go on dictating the layout.
    widestSlideAspect() {
      const measured = (this.slides || [])
        .filter((slide) => this.hasImage(slide))
        .map((slide) => {
          const dims = this.slideDims[slide.id];
          return {
            naturalWidth: dims ? dims.naturalWidth : null,
            naturalHeight: dims ? dims.naturalHeight : null,
          };
        });
      return maxAspectRatio(measured);
    },

    // Whether the hero renders as a FULL-BLEED BANNER (one edge-to-edge
    // image, no copy column) rather than as the #2613 split band.
    //
    // Derived from the ART, never from a distributor-facing setting: a
    // banner-shaped slide carries its own headline in its pixels (the comp's
    // "Travel + Leisure Company Store" hero is exactly this), so a live <h1>
    // beside it would duplicate copy the shopper can already read, and
    // confining it to a 45% media column shrinks a 1448px banner to a 551px
    // strip. Square-ish product art has neither problem and keeps the split.
    //
    // One mechanism -- "the hero renders the art it is given"
    // (docs/design-philosophy.md) -- rather than a mode flag a distributor
    // would have to be taught to set correctly.
    //
    // False while nothing is measured, so the shipped split band holds until
    // there is evidence for the stronger claim (principle 5: safe fallback).
    isBannerBand() {
      return isBannerAspect(this.widestSlideAspect);
    },

    // No-crop hero band height (px). The band is as tall as the WIDEST slide
    // when scaled to fill the column width; narrower slides fit with white
    // side padding. Holds the neutral fallback until width AND at least one
    // slide's aspect are known. Returns a bare number (Vuetify treats it as
    // px).
    //
    // A banner is capped at HERO_BANNER_MAX_HEIGHT instead: it spans the
    // viewport rather than a 45% column, so the split band's 380px cap would
    // crop a third of the artwork away.
    // A one-slide hero has nowhere to advance to. Vuetify would still
    // mount the prev/next controls and animate them in on hover -- over the
    // artwork -- for a click that does nothing, and would still run the
    // cycle timer on a single frame. Both are conditioned on this rather
    // than hidden with CSS: `display: none` would leave the buttons mounted
    // and the timer running, which is the same noise with extra steps.
    //
    // FIXED 2026-08-26 (Jake: "I thought we already fixed that, but I still
    // see them"). This guard shipped passing `undefined` for the negative
    // case, which does NOT suppress a prop -- it means "not provided", so
    // Vue applies the component's own default. Verified against the
    // INSTALLED Vuetify 3.11.6:
    //
    //   VCarousel.js:38  `showArrows: true`   <- the default undefined picks
    //   VWindow.js:223   renders controls when `showArrows !== false`
    //
    // So `undefined` re-enabled exactly what the ternary was written to
    // disable, and the arrows were showing on every single-slide hero. The
    // negative case must be an explicit `false`.
    //
    // `cycle` was NOT broken by the same shape -- it is a bare `Boolean`
    // (VCarousel.js:17) with no explicit default, so Vue casts an absent
    // value to `false` and `startTimeout` early-returns. It is written
    // explicitly anyway so the two props cannot drift apart, and so the
    // next reader does not have to re-derive which one was safe.
    hasMultipleSlides() {
      return (this.slides || []).length > 1;
    },

    heroBandHeight() {
      const computed = bandHeight(
        this.heroContainerWidth,
        this.widestSlideAspect,
        this.isBannerBand ? HERO_BANNER_MAX_HEIGHT : HERO_MAX_HEIGHT
      );
      return computed === null
        ? HERO_FALLBACK_HEIGHT
        : Math.round(computed);
    },

    // The width (px) a BANNER is actually drawn at, or null when it should
    // simply fill the band (viewport narrower than the height cap binds, or
    // nothing measured yet).
    //
    // This is what turns a vertical CROP into a side MARGIN. Once the
    // HERO_BANNER_MAX_HEIGHT cap binds, the band is shorter than the
    // banner's own ratio wants; drawing it at full band width would force
    // the fit to discard the overflow (the old `cover`) or to letterbox
    // top/bottom (a naive `contain`). Neither is what Jake asked for.
    // Capping the IMAGE's width to what the capped height can show whole
    // puts the leftover space at the left and right, which is.
    bannerImageWidth() {
      if (!this.isBannerBand) {
        return null;
      }
      const width = bannerRenderWidth(
        this.heroContainerWidth,
        this.widestSlideAspect,
        HERO_BANNER_MAX_HEIGHT
      );
      if (width === null) {
        return null;
      }
      // Only a width the cap actually CONSTRAINS is worth emitting; an
      // unconstrained banner keeps max-width: 100% so it still reaches both
      // viewport edges (the no-regression case).
      const measured = Number(this.heroContainerWidth);
      if (Number.isFinite(measured) && width >= measured - 0.5) {
        return null;
      }
      return Math.round(width);
    },
  },

  watch: {
    // The band element is v-if-gated on having at least one renderable
    // slide. Attach/tear down the ResizeObserver as the band appears and
    // disappears -- keyed on the SAME condition as the v-if so the observer
    // can't outlive the element it observes (or fail to attach to a band
    // that is in fact rendered).
    hasRenderableSlides: {
      immediate: false,
      handler(renderable) {
        if (renderable) {
          this.$nextTick(() => this.setupHeroResizeObserver());
        } else {
          this.teardownHeroResizeObserver();
        }
      },
    },
  },

  mounted() {
    if (this.hasRenderableSlides) {
      this.$nextTick(() => this.setupHeroResizeObserver());
    }
  },

  beforeUnmount() {
    this.teardownHeroResizeObserver();
  },

  methods: {
    // Whether a slide has an image worth binding to an <img src>. An empty
    // or whitespace-only URL is NOT inert: the browser resolves it against
    // the page URL, re-requests the document, fails to decode it as an
    // image, and paints a broken-image icon (and @load never fires, so the
    // band never leaves its neutral fallback height). A slide whose image
    // already failed to load is treated the same way.
    hasImage(slide) {
      if (!slide || this.failedSlideIds[slide.id]) return false;
      const url = slide.imageUrl;
      return typeof url === "string" && url.trim().length > 0;
    },

    // An image that 404s (deleted object, stale key) degrades to the same
    // placeholder as a slide that never had one.
    onSlideImageError(slideId) {
      if (this.failedSlideIds[slideId]) return;
      this.failedSlideIds = { ...this.failedSlideIds, [slideId]: true };
    },

    onSlideImageLoad(slideId, event) {
      const img = event && event.target;
      if (!img || !img.naturalWidth || !img.naturalHeight) {
        return;
      }
      this.slideDims = {
        ...this.slideDims,
        [slideId]: {
          naturalWidth: img.naturalWidth,
          naturalHeight: img.naturalHeight,
        },
      };
      // An image can finish loading before the ResizeObserver reports a
      // non-zero width; re-measure so the band leaves the neutral fallback
      // once BOTH width and this slide's aspect are known.
      this.$nextTick(() => this.measureHeroContainerWidth());
    },

    // Measure the hero band's rendered width. Safe to call repeatedly; a
    // zero width is ignored so the neutral fallback holds until a real width
    // is available.
    measureHeroContainerWidth() {
      const el = this.$refs.heroCarousel;
      if (el && el.clientWidth > 0) {
        this.heroContainerWidth = el.clientWidth;
      }
    },

    setupHeroResizeObserver() {
      const el = this.$refs.heroCarousel;
      if (!el) {
        return;
      }
      this.teardownHeroResizeObserver();
      this.measureHeroContainerWidth();
      if (typeof ResizeObserver !== "undefined") {
        this._heroResizeObserver = new ResizeObserver(() =>
          this.measureHeroContainerWidth()
        );
        this._heroResizeObserver.observe(el);
      }
    },

    teardownHeroResizeObserver() {
      if (this._heroResizeObserver) {
        this._heroResizeObserver.disconnect();
        this._heroResizeObserver = null;
      }
    },

    // Cap each image at its NATURAL size so it may downscale to fit but is
    // never upscaled. object-fit: contain (CSS) centers it with white padding.
    // min(100%, naturalPx) keeps whichever is smaller so a large image fits
    // the band AND is never scaled past its natural pixels.
    //
    // A FULL-BLEED BANNER is the one case where that cap is wrong. The
    // no-upscale rule exists because an upscaled photo looks blurry, and it
    // is the right trade when the alternative is a letterboxed slide in a
    // bounded column. But a banner's whole job is to reach both viewport
    // edges: capping the T+L banner at its natural 1448px would leave ~236px
    // of gutter on each side of a 1920px monitor. So a banner is still
    // allowed to scale PAST its natural width -- that half of the
    // 2026-08-15 decision stands.
    //
    // What changed (2026-08-26, Jake): the FIT. It was object-fit: cover,
    // which trims top/bottom once the band's height cap binds. On a wide
    // monitor that trim is not "a little" -- 177px at 2560px, 451px at
    // 3440px -- and when the headline is baked into the artwork it slices
    // the words in half. `contain` shows the whole banner instead, and
    // bannerImageWidth caps the drawn width so the leftover space lands as
    // MARGIN AT THE SIDES rather than as top/bottom letterboxing.
    //
    // Scoped strictly to the banner branch: non-banner slides keep the
    // never-upscale guard exactly as it shipped.
    slideImageStyle(slide) {
      const dims = this.slideDims[slide.id];
      if (!dims) {
        return {};
      }
      if (this.isBannerBand) {
        const capped = this.bannerImageWidth;
        return {
          width: "100%",
          height: "100%",
          // A bare 100% until the height cap binds, so the banner still
          // reaches both edges on ordinary desktop widths.
          maxWidth: capped === null ? "100%" : capped + "px",
          objectFit: "contain",
        };
      }
      return {
        maxWidth: "min(100%, " + dims.naturalWidth + "px)",
        maxHeight: "min(100%, " + dims.naturalHeight + "px)",
      };
    },
  },
};
</script>

<style scoped>
/* SPLIT BAND -----------------------------------------------------------
   Full-bleed band (so the photography reaches the viewport edge, as in the
   comp) whose INNER grid is width-capped by the shared .sf-container. The
   band's own light surface separates the hero from the white page below and
   gives the copy column something to sit on. */
.storefront-hero {
  width: 100%;
  background: var(--tf-surface-light, #f5f8fa);
  /* Thin brand rule under the topbar (the comp's navy hairline). Uses the
     raw brand color -- a non-text accent, so WCAG 1.4.11's 3:1 is the bar,
     not AA text contrast. Falls back to the app primary when the store has
     no brand color. */
  border-top: 2px solid var(--sf-brand-color, var(--tf-primary, #119ae9));
  margin-bottom: 8px;
}

/* Two columns: copy ~55%, media ~45% -- the ratio in the comp, and the
   reason the small source art works here (see the component header). */
.storefront-hero-band {
  display: flex;
  align-items: center;
  gap: 32px;
  padding: 0 32px;
}

/* FULL-BLEED BANNER ------------------------------------------------------
   A banner-shaped slide (aspect >= BANNER_ASPECT_THRESHOLD) spans the whole
   viewport: no copy column, no width cap, no side padding.

   Full-bleed here is achieved by simply NOT applying .sf-container, rather
   than by any 100vw / negative-margin trick. .storefront-hero is already an
   edge-to-edge block (the page renders it outside its own .sf-container --
   HomePage.vue), so the band is naturally viewport-wide and the width cap is
   the only thing holding it in. Dropping the cap is therefore the whole fix.

   This matters for correctness, not just simplicity: `width: 100vw` measures
   the viewport INCLUDING the classic scrollbar gutter, so on any page tall
   enough to scroll a 100vw band is ~15px wider than its containing block and
   induces a horizontal scrollbar. A plain 100%-of-the-block band cannot
   overflow, at any width. */
.storefront-hero-band--banner {
  display: block;
  padding: 0;
  gap: 0;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
}

/* The media column is the whole band in banner mode. Stated explicitly
   rather than left to fall out of `display: block` neutralising the
   `flex: 1 1 45%` below: the 45% is a flex-BASIS, so it is inert here only
   for as long as the banner band stays non-flex. Making the width explicit
   means the banner cannot silently narrow to 45% if this band ever gains a
   flex/grid rule -- and the measured width feeds the band HEIGHT
   (measureHeroContainerWidth reads this element), so a wrong width here
   would quietly produce a wrong height too. */
.storefront-hero-band--banner .storefront-hero-media {
  flex: 1 1 100%;
  width: 100%;
}

.storefront-hero-copy {
  flex: 1 1 55%;
  min-width: 0;
  padding: 32px 0;
}

.storefront-hero-media {
  flex: 1 1 45%;
  min-width: 0;
}

.storefront-hero-headline {
  margin: 0;
  font-size: clamp(2rem, 4vw, 3.25rem);
  line-height: 1.1;
  font-weight: 700;
  letter-spacing: -0.02em;
  /* Derived AA-safe ink from the store's brand color (see
     utils/storefrontBrandAccent.js). --tf-fg-1 covers the no-brand-color
     case; --sf-brand-ink is guaranteed >=4.5:1 against the light surface
     this band paints, which is the SAME surface the util derives against. */
  color: var(--sf-brand-ink, var(--tf-fg-1, #1c1c1c));
  /* Long single-word store names must not overflow the column. */
  overflow-wrap: anywhere;
}

/* Below 960px the split collapses to a stack (copy above media, straight
   from document order) -- the two-column hero is a wide-viewport
   affordance, and a 45% media column on a phone would be unreadable. */
@media (max-width: 960px) {
  .storefront-hero-band {
    flex-direction: column;
    align-items: stretch;
    gap: 0;
    padding: 0 16px;
  }

  /* The banner has no columns to stack and no copy to inset -- it stays
     edge-to-edge on a phone exactly as it is on a desktop, just shorter
     (the band height is derived from the measured width, so it tracks the
     viewport down automatically). Re-stating padding: 0 here because the
     rule above is same-specificity and later in the sheet, so it would
     otherwise put 16px gutters back on a full-bleed band. */
  .storefront-hero-band--banner {
    padding: 0;
  }

  .storefront-hero-copy {
    flex: 1 1 auto;
    padding: 24px 0 8px;
  }

  .storefront-hero-media {
    flex: 1 1 auto;
    padding-bottom: 16px;
  }

  /* Same reason as the padding reset above: the split band's 16px
     bottom inset would show as a strip of band surface under a
     full-bleed banner, which is the one edge the banner must not have. */
  .storefront-hero-band--banner .storefront-hero-media {
    padding-bottom: 0;
  }
}

.storefront-hero-carousel {
  width: 100%;
}

.storefront-hero-slide-link {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  text-decoration: none;
}

.storefront-hero-slide-image {
  object-fit: contain;
  /* Transparent, not #fff: the image now sits on the band's light surface
     rather than on the page's white, so a hardcoded white plate behind a
     letterboxed (contain) slide would show as a bright rectangle against
     the band. Slides with their own opaque background are unaffected. */
  background: transparent;
  max-width: 100%;
  max-height: 100%;
}

/* Stand-in for a slide with no usable image. A quiet tint of the band's own
   surface -- it reads as "nothing here yet" rather than as an error state or
   a broken asset, and it fills the same box the image would have so the
   carousel's rhythm is unchanged. No icon and no copy: the slide may carry
   its own headline, and a second message would compete with it.

   --tf-surface-dark ("subdued panels", #e8eff5) rather than -surface-muted
   (#fafafa): the band this sits on is --tf-surface-light (#f5f8fa), against
   which #fafafa is indistinguishable -- the placeholder has to READ as a
   deliberate block, not as an invisible gap. */
.storefront-hero-slide-placeholder {
  width: 100%;
  height: 100%;
  background: var(--tf-surface-dark, #e8eff5);
  border-radius: 4px;
}

.storefront-hero-slide-headline {
  position: absolute;
  bottom: 12px;
  left: 50%;
  transform: translateX(-50%);
  padding: 6px 16px;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  font-size: 1.1rem;
  font-weight: 600;
  border-radius: 4px;
  max-width: 90%;
  text-align: center;
}

/* De-circle the hero carousel arrows.
   REAL SELECTOR, verified against the installed Vuetify 3.7.5: <v-carousel>
   delegates prev/next entirely to VWindow, which puts the class on the VBtn
   ROOT itself as ``.v-window__left`` / ``.v-window__right``. There is NO
   ``.v-carousel__prev`` / ``.v-carousel__next`` ancestor in the rendered DOM
   -- `grep -roE "v-window__(left|right)|v-carousel__(prev|next)"
   frontend/node_modules/vuetify/` returns 24 hits for the former and ZERO for
   the latter. The app-wide rule in style.css:204-207 still targets the dead
   selector and sets only ``color``, so the elevated circular VBtn chrome
   survives -- measured live as a 48x48 white elevated button sitting on the
   hero image. That global rule is deliberately left alone (retargeting it
   would restyle every carousel app-wide, unscoped and unverified); each
   surface is fixed under its own class instead, matching the store-editor
   ProductCard (#2577) fix and the same fix made in the since-removed
   StorefrontProductCard (#2582, component deleted as dead code 2026-08-29).

   De-circle ONLY -- deliberately NOT edge-flush. Edge-flush was a specific
   ask for the store-editor card's own layout; the hero is a centered,
   width-capped image where the default inset reads correctly. */
.storefront-hero :deep(.v-window__left),
.storefront-hero :deep(.v-window__right) {
  background: transparent !important;
  box-shadow: none !important;
  border: none !important;
  /* Near-white, matching the app-wide storefront-carousel color in
     style.css:206. NOT a --tf-fg-* token: those are tuned for contrast against
     LIGHT surfaces, and these arrows sit on arbitrary merchandising
     PHOTOGRAPHY. --tf-fg-4 (#636363) would vanish on a dark slide. The
     drop-shadow keeps the glyph legible on a light image, which flat white
     alone does not. */
  color: #e5e8e8 !important;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.55));
}
</style>
